import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {usePermissions} from "tmwx-design-common";
import {useEffect, useMemo, useState} from "react";
import {Alert, Button, Grid, Header, Input, styles, Surface, Table, Text, useTheme} from '../../tmwx-design-web/src';
import {useNavigate, useSearchParams} from "react-router-dom";
import {checkFieldError, isEmpty} from "gui-common/appHelper";
import {RootState} from "gui-common/redux/store";
import {
    addNewDeviceParam,
    clearDeviceParamError,
    clearDeviceParamResponse,
    getDeviceParamsList
} from "gui-common/redux/action/efe/deviceParamAction";
import {ColumnDef} from "@tanstack/react-table";
import {ResponseBase} from "gui-common/redux/data";
import {getFieldErrorText} from "../../utils/helper";
import {setSnackbarData} from "gui-common/redux/action/snackbarAction";
import {
    AddParamRequest,
    ParamItem
} from "gui-common/redux/data/deviceParam";

type DeviceParamFormState = {
    systemId: string;
    name: string;
    jsonText: string;
};

const initialDeviceParamFormState: DeviceParamFormState = {
    systemId: "",
    name: "",
    jsonText: ""
};

const formatJsonValue = (value: unknown) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "string") {
        return value;
    }

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const DeviceParams = () => {
    const { t } = useTranslation()
    const dispatch: any = useDispatch();
    const { padding } = useTheme()
    const { hasRights } = usePermissions()
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isLoading = useSelector<RootState, boolean>(state => state.deviceParam.isLoading);
    const deviceParamList = useSelector<RootState, ParamItem[]>(state => state.deviceParam.commands)
    const response = useSelector<RootState, ResponseBase | null>(state => state.deviceParam.response)
    const serverError = useSelector<RootState, ResponseBase | null>(state => state.deviceParam.error)
    const [request, setRequest] = useState<DeviceParamFormState>(initialDeviceParamFormState)
    const [validationMessage, setValidationMessage] = useState<string>("");
    const hasCreateRights = hasRights(['efe_efe_param_create'])

    const handleGetList = () => {
        const systemId = searchParams.get("systemId") as string;
        if (!isEmpty(systemId) && hasRights(["efe_efe_param_list"])) {
            dispatch(getDeviceParamsList(systemId))
        }
    }

    const handleClearRequest = () => {
        setValidationMessage('')
        dispatch(clearDeviceParamError())
        dispatch(clearDeviceParamResponse())
        setRequest(prev => ({
            ...prev,
            name: '',
            jsonText: ''
        }))
    }

    const handleAdd = () => {
        setValidationMessage('')
        dispatch(clearDeviceParamError())
        dispatch(clearDeviceParamResponse())

        if (isEmpty(request.systemId) || isEmpty(request.name) || isEmpty(request.jsonText)) {
            setValidationMessage(t('mandatoryFields'));
            return;
        }

        try {
            const parsedJson = JSON.parse(request.jsonText);
            const payload: AddParamRequest = {
                systemId: request.systemId,
                name: request.name,
                json: parsedJson
            };

            dispatch(addNewDeviceParam(payload))
        } catch {
            setValidationMessage(t('invalidJson'));
        }
    }

    useEffect(() => {
        const systemId = searchParams.get("systemId") as string;
        if (!isEmpty(systemId) && hasRights(["efe_efe_param_list"])) {
            dispatch(getDeviceParamsList(systemId))
            setRequest(prev => ({ ...prev, systemId }))
        }
    }, [searchParams])

    /* HANDLE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                handleClearRequest()
                handleGetList()
                dispatch(setSnackbarData({
                    status: 'success',
                    text: t('actionSuccess')
                }))
            }
        }
    }, [response])

    const columns = useMemo<ColumnDef<ParamItem>[]>(
        () => [
            {
                accessorKey: 'opVersion',
                meta: t('atr.opVersion.paramVersion'),
                header: () => <Text bold>{t('atr.opParams.paramVersion')}</Text>,
                size: 70,
                cell: (cell) => (
                    <Text>
                        {cell.getValue()}
                    </Text>
                )
            },
            {
                accessorKey: 'name',
                meta: t('name'),
                header: () => <Text bold>{t('name')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.name}
                    </Text>
                )
            },
            {
                accessorKey: 'json',
                meta: t('json'),
                header: () => <Text bold>{t('json')}</Text>,
                cell: ({ row }) => (
                    <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {formatJsonValue(row.original.json)}
                    </Text>
                )
            },
            {
                accessorKey: 'requestDate',
                meta: t('atr.opParams.requestTime'),
                header: () => <Text bold>{t('atr.opParams.requestTime')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.requestDate ? row.original.requestDate.split('.')[0].replaceAll('T', ' ') : ''}
                    </Text>
                )
            },
            {
                accessorKey: 'responseDate',
                meta: t('atr.opParams.responseTime'),
                header: () => <Text bold>{t('atr.opParams.responseTime')}</Text>,
                cell: ({ row }) => (
                    <Text>
                        {row.original.responseDate ? row.original.responseDate.split('.')[0].replaceAll('T', ' ') : ''}
                    </Text>
                )
            }
        ],
        [dispatch, hasRights, padding, t]
    );

    return (
        <Surface style={{ height: '100vh' }} elevation={3}>
            <Header
                title={t('atr.opParams.opParamsText')}
                subtitle={searchParams.get("systemId")}
                rounded
                actionLeft={<Button iconLeft={'arrow_back'} status='neutral' variant='ghost' onPress={() => {
                    handleClearRequest()
                    navigate(- 1)
                }} />}
            />
            <Grid.Container maxWidth='xl' centered>
                <Grid.Col m={hasCreateRights ? 8 : 12} lg={hasCreateRights ? 8 : 12} xl={hasCreateRights ? 8 : 12}>
                    <Surface rounded>
                        <Table
                            columns={columns}
                            data={deviceParamList}
                            loading={isLoading}
                            refreshData={() => handleGetList()}
                            dense
                            useGlobalFilter
                            labels={{
                                add: t('add'),
                                deletePreferences: t('deletePreferences'),
                                jumpHere: t('jumpHere'),
                                page: t('page'),
                                refresh: t('refresh'),
                                row: t('row'),
                                rowsSelected: t('rowsSelected'),
                                savePreferencies: t('savePreferences'),
                                searchData: t('searchData'),
                                selectCurrentPageData: t('selectCurrentPageData'),
                                showAll: t('showAll'),
                                sizing: t('sizing')
                            }}
                        />
                    </Surface>
                </Grid.Col>

                {hasCreateRights && (
                    <Grid.Col m={4} lg={4} xl={4}>
                        <Surface>
                            <Text bold>
                                {t('atr.opParams.addOpParam')}
                            </Text>

                            {serverError && (
                                <Alert style={styles.marginTop8} status="danger" text={t('errorOccurred')} />
                            )}

                            {!isEmpty(validationMessage) && (
                                <Alert style={styles.marginTop8} text={validationMessage} status="danger" />
                            )}

                            <Input
                                style={styles.marginTop8}
                                fullWidth
                                error={checkFieldError('name', serverError)}
                                icon={"id_card_outlined"}
                                placeholder={t('name')}
                                value={request.name}
                                onChangeValue={(text) => setRequest(prev => ({ ...prev, name: text }))}
                                helperText={getFieldErrorText('name', serverError, t)}
                            />

                            <Input
                                style={styles.marginTop8}
                                fullWidth
                                error={checkFieldError('json', serverError)}
                                icon={"data_object_outlined"}
                                placeholder={t('json')}
                                multiline
                                numberOfLines={8}
                                value={request.jsonText}
                                onChangeValue={(text) => setRequest(prev => ({ ...prev, jsonText: text }))}
                                helperText={getFieldErrorText('json', serverError, t)}
                            />

                            <div style={{ ...styles.row, ...styles.marginTop8, ...{ justifyContent: 'flex-end' } }}>
                                <Button status="danger" variant="outlined" onPress={handleClearRequest}>
                                    {t('cancel')}
                                </Button>

                                <Button style={styles.marginLeft8} onPress={handleAdd}>
                                    {t('add')}
                                </Button>
                            </div>
                        </Surface>
                    </Grid.Col>
                )}
            </Grid.Container>
        </Surface>
    )
}
export default DeviceParams;
